/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Attribute, ChangeDetectorRef, ComponentFactoryResolver, ComponentRef, Directive, EventEmitter, Injector, OnDestroy, OnInit, Output, ViewContainerRef} from '@angular/core';

import {ChildrenOutletContexts} from '../router_outlet_context';
import {ActivatedRoute} from '../router_state';
import {PRIMARY_OUTLET} from '../shared';
/**
 * \@whatItDoes Acts as a placeholder that Angular dynamically fills based on the current router
 * state.
 * 
 * \@howToUse 
 * 
 * ```
 * <router-outlet></router-outlet>
 * <router-outlet name='left'></router-outlet>
 * <router-outlet name='right'></router-outlet>
 * ```
 * 
 * A router outlet will emit an activate event any time a new component is being instantiated,
 * and a deactivate event when it is being destroyed.
 * 
 * ```
 * <router-outlet
 *   (activate)='onActivate($event)'
 *   (deactivate)='onDeactivate($event)'></router-outlet>
 * ```
 * \@ngModule RouterModule
 * 
 * \@stable
 */
export class RouterOutlet implements OnDestroy, OnInit {
private activated: ComponentRef<any>|null = null;
private _activatedRoute: ActivatedRoute|null = null;
private name: string;

   activateEvents = new EventEmitter<any>();
   deactivateEvents = new EventEmitter<any>();
/**
 * @param {?} parentContexts
 * @param {?} location
 * @param {?} resolver
 * @param {?} name
 * @param {?} changeDetector
 */
constructor(
private parentContexts: ChildrenOutletContexts,
private location: ViewContainerRef,
private resolver: ComponentFactoryResolver,  name: string,
private changeDetector: ChangeDetectorRef) {
    this.name = name || PRIMARY_OUTLET;
    parentContexts.onChildOutletCreated(this.name, this);
  }
/**
 * @return {?}
 */
ngOnDestroy(): void { this.parentContexts.onChildOutletDestroyed(this.name); }
/**
 * @return {?}
 */
ngOnInit(): void {
    if (!this.activated) {
      // If the outlet was not instantiated at the time the route got activated we need to populate
      // the outlet when it is initialized (ie inside a NgIf)
      const /** @type {?} */ context = this.parentContexts.getContext(this.name);
      if (context && context.route) {
        if (context.attachRef) {
          // `attachRef` is populated when there is an existing component to mount
          this.attach(context.attachRef, context.route);
        } else {
          // otherwise the component defined in the configuration is created
          this.activateWith(context.route, context.resolver || null);
        }
      }
    }
  }
/**
 * @deprecated since v4 *
 * @return {?}
 */
get locationInjector(): Injector { return this.location.injector; }
/**
 * @deprecated since v4 *
 * @return {?}
 */
get locationFactoryResolver(): ComponentFactoryResolver { return this.resolver; }
/**
 * @return {?}
 */
get isActivated(): boolean { return !!this.activated; }
/**
 * @return {?}
 */
get component(): Object {
    if (!this.activated) throw new Error('Outlet is not activated');
    return this.activated.instance;
  }
/**
 * @return {?}
 */
get activatedRoute(): ActivatedRoute {
    if (!this.activated) throw new Error('Outlet is not activated');
    return /** @type {?} */(( this._activatedRoute as ActivatedRoute));
  }
/**
 * @return {?}
 */
get activatedRouteData() {
    if (this._activatedRoute) {
      return this._activatedRoute.snapshot.data;
    }
    return {};
  }
/**
 * Called when the `RouteReuseStrategy` instructs to detach the subtree
 * @return {?}
 */
detach(): ComponentRef<any> {
    if (!this.activated) throw new Error('Outlet is not activated');
    this.location.detach();
    const /** @type {?} */ cmp = this.activated;
    this.activated = null;
    this._activatedRoute = null;
    return cmp;
  }
/**
 * Called when the `RouteReuseStrategy` instructs to re-attach a previously detached subtree
 * @param {?} ref
 * @param {?} activatedRoute
 * @return {?}
 */
attach(ref: ComponentRef<any>, activatedRoute: ActivatedRoute) {
    this.activated = ref;
    this._activatedRoute = activatedRoute;
    this.location.insert(ref.hostView);
  }
/**
 * @return {?}
 */
deactivate(): void {
    if (this.activated) {
      const /** @type {?} */ c = this.component;
      this.activated.destroy();
      this.activated = null;
      this._activatedRoute = null;
      this.deactivateEvents.emit(c);
    }
  }
/**
 * @param {?} activatedRoute
 * @param {?} resolver
 * @return {?}
 */
activateWith(activatedRoute: ActivatedRoute, resolver: ComponentFactoryResolver|null) {
    if (this.isActivated) {
      throw new Error('Cannot activate an already activated outlet');
    }
    this._activatedRoute = activatedRoute;
    const /** @type {?} */ snapshot = activatedRoute._futureSnapshot;
    const /** @type {?} */ component = /** @type {?} */(( <any> /** @type {?} */((snapshot._routeConfig)).component));
    resolver = resolver || this.resolver;
    const /** @type {?} */ factory = resolver.resolveComponentFactory(component);
    const /** @type {?} */ childContexts = this.parentContexts.getOrCreateContext(this.name).children;
    const /** @type {?} */ injector = new OutletInjector(activatedRoute, childContexts, this.location.injector);
    this.activated = this.location.createComponent(factory, this.location.length, injector);
    // Calling `markForCheck` to make sure we will run the change detection when the
    // `RouterOutlet` is inside a `ChangeDetectionStrategy.OnPush` component.
    this.changeDetector.markForCheck();
    this.activateEvents.emit(this.activated.instance);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: 'router-outlet', exportAs: 'outlet'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChildrenOutletContexts, },
{type: ViewContainerRef, },
{type: ComponentFactoryResolver, },
{type: undefined, decorators: [{ type: Attribute, args: ['name', ] }, ]},
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'activateEvents': [{ type: Output, args: ['activate', ] },],
'deactivateEvents': [{ type: Output, args: ['deactivate', ] },],
};
}

function RouterOutlet_tsickle_Closure_declarations() {
/** @type {?} */
RouterOutlet.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RouterOutlet.ctorParameters;
/** @type {?} */
RouterOutlet.propDecorators;
/** @type {?} */
RouterOutlet.prototype.activated;
/** @type {?} */
RouterOutlet.prototype._activatedRoute;
/** @type {?} */
RouterOutlet.prototype.name;
/** @type {?} */
RouterOutlet.prototype.activateEvents;
/** @type {?} */
RouterOutlet.prototype.deactivateEvents;
/** @type {?} */
RouterOutlet.prototype.parentContexts;
/** @type {?} */
RouterOutlet.prototype.location;
/** @type {?} */
RouterOutlet.prototype.resolver;
/** @type {?} */
RouterOutlet.prototype.changeDetector;
}

class OutletInjector implements Injector {
/**
 * @param {?} route
 * @param {?} childContexts
 * @param {?} parent
 */
constructor(
private route: ActivatedRoute,
private childContexts: ChildrenOutletContexts,
private parent: Injector) {}
/**
 * @param {?} token
 * @param {?=} notFoundValue
 * @return {?}
 */
get(token: any, notFoundValue?: any): any {
    if (token === ActivatedRoute) {
      return this.route;
    }

    if (token === ChildrenOutletContexts) {
      return this.childContexts;
    }

    return this.parent.get(token, notFoundValue);
  }
}

function OutletInjector_tsickle_Closure_declarations() {
/** @type {?} */
OutletInjector.prototype.route;
/** @type {?} */
OutletInjector.prototype.childContexts;
/** @type {?} */
OutletInjector.prototype.parent;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
